/** A shared inbox's thread list, named once so SharedInboxView and the
 *  background warm fill the same per-user entry in the offline email store.
 *  The warm reads only the inbox this person last opened: it never lists
 *  inboxes, nor reads one nobody here uses. */

import { sharedInboxService, type SharedInboxThreadSummary } from '../../services/SharedInboxService';
import { emailCacheGeneration, writeEmailCache } from '../../services/emailOfflineStore';
import {
  currentBackgroundReadScope,
  peekBackgroundRead,
  rememberBackgroundRead,
  type BackgroundReadScope,
} from '../../services/BackgroundReadCache';

const LAST_OPENED = 'email/last-shared-inbox';

export const sharedThreadsCacheKey = (userId: string, workspaceId: string, groupId: string) =>
  `${userId}::${workspaceId}::${groupId}::all`;

/** The inbox's first page of threads, newest first. `keep` stores them for the
 *  next first paint, unless this device signed out while they were in flight. */
export async function loadSharedThreads(userId: string, workspaceId: string, groupId: string): Promise<{ rows: SharedInboxThreadSummary[]; keep: () => void }> {
  const started = emailCacheGeneration();
  const rows = await sharedInboxService.listThreads({ workspaceId, groupId });
  return { rows, keep: () => { void writeEmailCache('sharedThreads', sharedThreadsCacheKey(userId, workspaceId, groupId), rows, started); } };
}

/** Records the inbox just read; its age is also the age of that inbox's stored threads. */
export function rememberSharedInbox(userId: string, workspaceId: string, groupId: string): void {
  const scope = currentBackgroundReadScope(workspaceId);
  if (scope?.actorId === userId) rememberBackgroundRead(scope, LAST_OPENED, groupId);
}

/** The warm's task: the last opened inbox once its stored threads are older than `maxAgeMs`. */
export async function warmLastSharedInbox(scope: BackgroundReadScope, maxAgeMs: number): Promise<void> {
  const last = peekBackgroundRead<string>(scope, LAST_OPENED);
  if (!last || typeof last.data !== 'string' || Date.now() - last.at < maxAgeMs) return;
  (await loadSharedThreads(scope.actorId, scope.workspaceId, last.data)).keep();
  rememberSharedInbox(scope.actorId, scope.workspaceId, last.data);
}
